/**
 * The measurements the desktop orders grid is laid out to, in column order.
 *
 * One list, read by three things that have to agree: the grid's `columns` in `OrdersList`, the
 * visibility model it hands the grid, and the placeholder cells in `OrdersListSkeleton`. Stated here
 * rather than in any one of them because a `flex` changed in the grid but not in the skeleton shows
 * up as placeholders sitting under the wrong columns — and only while loading, which is exactly when
 * nobody is looking closely.
 */
export type OrderColumnSpec = {
  field: string;
  flex: number;
  minWidth: number;
  /**
   * Hidden between sm and md. All three such columns are on the order detail page, and dropping them
   * takes the grid's minimum width from ~975px down to ~545px so it still fits a 600px viewport —
   * which also leaves those rows a chip tall, since the two-line customer cell is what a row is
   * otherwise measured from.
   */
  tabletHidden?: boolean;
};

export const ORDER_COLUMN_SPECS: OrderColumnSpec[] = [
  { field: 'id', flex: 0.5, minWidth: 120 },
  { field: 'deliveryContact', flex: 1.6, minWidth: 230, tabletHidden: true },
  { field: 'orderPlaced', flex: 0.8, minWidth: 150 },
  { field: 'deliveryType', flex: 0.6, minWidth: 115, tabletHidden: true },
  { field: 'orderStatusString', flex: 1.2, minWidth: 190 },
  { field: 'shoppingCartPrice', flex: 0.4, minWidth: 85, tabletHidden: true },
  { field: 'grandTotal', flex: 0.5, minWidth: 85 },
];

const SPEC_BY_FIELD: Record<string, OrderColumnSpec> = Object.fromEntries(
  ORDER_COLUMN_SPECS.map((spec) => [spec.field, spec])
);

/**
 * The three properties a `GridColDef` takes from the spec, and deliberately not `tabletHidden` —
 * that one is ours and MUI has no use for it. Spread into a column definition, which then adds its
 * header and its cell renderer.
 */
export const gridColumn = (field: string): { field: string; flex: number; minWidth: number } => {
  const { flex, minWidth } = SPEC_BY_FIELD[field];

  return { field, flex, minWidth };
};

/** What the grid's `columnVisibilityModel` gets between sm and md. */
export const TABLET_HIDDEN_COLUMNS: Record<string, boolean> = Object.fromEntries(
  ORDER_COLUMN_SPECS.filter((spec) => spec.tabletHidden).map((spec) => [spec.field, false])
);

export const ALL_COLUMNS_VISIBLE = {};

/**
 * The air above and below a grid cell's content. MUI gives cells `0 10px` and nothing vertical, and
 * the orders grid measures each row from its tallest cell (`getRowHeight: () => 'auto'`) — so without
 * this a row was exactly as tall as a chip, with the text pressed against the dividers. `OrdersList`
 * applies it to the real rows and `OrdersListSkeleton` to the placeholders, which is what makes the
 * two the same height.
 */
export const GRID_CELL_PADDING_Y = 6;
